import React, { useEffect, useState } from 'react';
import { FurnitureShape as IFurnitureShape } from '../../../../types';
import { fetchSvgText } from '../../../../utils/svgAssetCache';
import { DEFAULT_FONT_FAMILY, DEFAULT_FURNITURE_FILL_COLOR } from '../../defines';
import { useShape } from '../ShapeContext/useShape';

// Parsed dimensions per asset url — DOMParser is expensive and every
// shape referencing the same asset would otherwise re-parse it.
const dimensionsCache = new Map<string, { width: number; height: number }>();

// Function to extract SVG dimensions
const getSVGDimensions = (svgContent: string): { width: number; height: number } => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgContent, 'image/svg+xml');
  const svgElement = doc.querySelector('svg');

  if (svgElement) {
    const width = svgElement.getAttribute('width');
    const height = svgElement.getAttribute('height');
    const viewBox = svgElement.getAttribute('viewBox');

    if (width && height) {
      return {
        width: parseFloat(width),
        height: parseFloat(height)
      };
    } else if (viewBox) {
      const [, , vbWidth, vbHeight] = viewBox.split(' ').map(Number);
      return {
        width: vbWidth,
        height: vbHeight
      };
    }
  }

  // Default fallback
  return { width: 64, height: 64 };
};

export const FurnitureShapeSVG: React.FC = () => {
  const { shape } = useShape();
  const furnitureShape = shape as IFurnitureShape;
  const [svgContent, setSvgContent] = useState<string>('');
  const [svgDimensions, setSvgDimensions] = useState({ width: 64, height: 64 });
  const fillColor = shape.color || DEFAULT_FURNITURE_FILL_COLOR;
  const imgUrl = furnitureShape?.furniture?.url;

  useEffect(() => {
    if (!imgUrl) return;
    let cancelled = false;

    // Shared session cache — one fetch per url regardless of how many
    // shapes reference the same asset (concurrent mounts included).
    fetchSvgText(imgUrl)
      .then((svgText) => {
        if (cancelled) return;
        setSvgContent(svgText.replace(/currentColor/g, fillColor));
        let dimensions = dimensionsCache.get(imgUrl);
        if (!dimensions) {
          dimensions = getSVGDimensions(svgText);
          dimensionsCache.set(imgUrl, dimensions);
        }
        setSvgDimensions(dimensions);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [imgUrl, fillColor]);

  // Match Konva's transform pipeline (translate · rotate · scale) so flipped
  // and rotated shapes render at the same visual position as the editor.
  const sX = shape.scaleX || 1;
  const sY = shape.scaleY || 1;
  const rotPart = shape.rotation ? ` rotate(${shape.rotation})` : '';
  const scalePart = sX !== 1 || sY !== 1 ? ` scale(${sX}, ${sY})` : '';
  const transform = `translate(${shape.x}, ${shape.y})${rotPart}${scalePart}`;

  // Calculate the scale factors based on the actual SVG dimensions and desired shape size
  const scaleX = shape.width / svgDimensions.width;
  const scaleY = shape.height / svgDimensions.height;

  // Calculate text position and alignment
  const textX = (() => {
    if (furnitureShape.textAlign === 'center') {
      return shape.width / 2;
    } else if (furnitureShape.textAlign === 'right') {
      return shape.width;
    } else {
      return 0;
    }
  })();

  const textAnchor = furnitureShape.textAlign === 'center' ? 'middle' : furnitureShape.textAlign === 'right' ? 'end' : 'start';

  return (
    <g transform={transform}>
      {/* Render the furniture SVG */}
      <g
        dangerouslySetInnerHTML={{ __html: svgContent }}
        transform={`scale(${scaleX}, ${scaleY})`}
        opacity={shape.locked ? 0.5 : 1}
      />
      
      {/* Render the text if present */}
      {furnitureShape.text && (
        <text
          x={textX}
          y={furnitureShape.height + 10}
          style={{
            fontSize: `${furnitureShape.fontSize || 20}px`,
            fontFamily: furnitureShape.fontFamily || DEFAULT_FONT_FAMILY,
            fontWeight: furnitureShape.isBold ? '700' : '300',
            fontStyle: furnitureShape.isItalic ? 'italic' : 'normal',
            textDecoration: furnitureShape.isUnderline ? 'underline' : 'none',
            fill: furnitureShape.textColor || '#B5B5B5'
          }}
          textAnchor={textAnchor}
          dominantBaseline="hanging"
        >
          {furnitureShape.text.split('\n').map((line, i) => (
            <tspan
              key={i}
              x={textX}
              dy={i === 0 ? 0 : '1.2em'}
              dominantBaseline="hanging"
            >
              {line}
            </tspan>
          ))}
        </text>
      )}
    </g>
  );
}; 